import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Package, ShoppingBag, LogOut, Instagram, Trash2, Film, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ProductImageManager from "../components/ProductImageManager";
import OrdersAdmin from "../components/OrdersAdmin";

export default function AdminPage() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [igPosts, setIgPosts] = useState([]);
  const [igForm, setIgForm] = useState({ image_url: "", post_url: "", caption: "", order: 0 });
  const [showForm, setShowForm] = useState(false);
  const [editingImages, setEditingImages] = useState(null); // product id
  const [videoUrl, setVideoUrl] = useState("");
  const [videoInput, setVideoInput] = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", category: "apparel", price: "", description: "", images: "", variants: "" });

  const reload = async () => {
    const [p, o, ig] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/orders"),
      api.get("/instagram/posts"),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setIgPosts(ig.data);
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        slug: form.slug,
        category: form.category,
        price: parseFloat(form.price),
        description: form.description,
        images: form.images.split(",").map(s => s.trim()).filter(Boolean),
        variants: form.variants ? form.variants.split(",").map(s => ({ label: s.trim(), stock: 10 })) : [],
        featured: false,
        active: true,
      };
      await api.post("/admin/products", payload);
      toast.success("Product created");
      setForm({ name: "", slug: "", category: "apparel", price: "", description: "", images: "", variants: "" });
      setShowForm(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deactivate = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deactivated");
    reload();
  };

  const submitIg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/instagram/posts", {
        image_url: igForm.image_url.trim(),
        post_url: igForm.post_url.trim(),
        caption: igForm.caption,
        order: parseInt(igForm.order) || 0,
      });
      toast.success("Instagram post added");
      setIgForm({ image_url: "", post_url: "", caption: "", order: 0 });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deleteIg = async (id) => {
    await api.delete(`/admin/instagram/posts/${id}`);
    toast.success("Removed");
    reload();
  };

  const saveVideo = async () => {
    try {
      await api.put("/admin/settings/artist-video", { video_url: videoInput || null });
      toast.success("Artist video updated");
      setVideoUrl(videoInput);
    } catch (e) { toast.error("Failed"); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "artist-video");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${api.defaults.baseURL.replace("/api","")}${data.url}`;
      setVideoInput(fullUrl);
      await api.put("/admin/settings/artist-video", { video_url: fullUrl });
      setVideoUrl(fullUrl);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setVideoUploading(false); }
  };

  // Auth still resolving — show a placeholder so the page isn't blank.
  if (loading) {
    return (
      <div data-testid="admin-loading" className="max-w-[1400px] mx-auto px-6 py-24 text-center opacity-70">
        <Loader2 size={20} className="animate-spin mx-auto mb-3" />
        Loading admin…
      </div>
    );
  }

  // Not signed in as an admin — redirect straight to the staff login page.
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
      <div className="flex items-start justify-between mb-10">
        <div>
          <div className="label-eyebrow mb-3">Studio control</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none">Admin.</h1>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="btn-secondary inline-flex items-center gap-2"><LogOut size={16}/> Sign out</button>
      </div>

      <div className="flex gap-2 mb-8">
        <button data-testid="admin-tab-products" onClick={() => setTab("products")} className={`px-5 py-2 rounded-full text-sm border ${tab === "products" ? "" : "opacity-60"}`} style={{ borderColor: "var(--kaiya-text)", background: tab === "products" ? "var(--kaiya-text)" : "transparent", color: tab === "products" ? "var(--kaiya-bg)" : "var(--kaiya-text)" }}>
          <ShoppingBag size={14} className="inline mr-2" /> Products ({products.length})
        </button>
        <button data-testid="admin-tab-orders" onClick={() => setTab("orders")} className={`px-5 py-2 rounded-full text-sm border ${tab === "orders" ? "" : "opacity-60"}`} style={{ borderColor: "var(--kaiya-text)", background: tab === "orders" ? "var(--kaiya-text)" : "transparent", color: tab === "orders" ? "var(--kaiya-bg)" : "var(--kaiya-text)" }}>
          <Package size={14} className="inline mr-2" /> Orders ({orders.length})
        </button>
        <button data-testid="admin-tab-instagram" onClick={() => setTab("instagram")} className={`px-5 py-2 rounded-full text-sm border ${tab === "instagram" ? "" : "opacity-60"}`} style={{ borderColor: "var(--kaiya-text)", background: tab === "instagram" ? "var(--kaiya-text)" : "transparent", color: tab === "instagram" ? "var(--kaiya-bg)" : "var(--kaiya-text)" }}>
          <Instagram size={14} className="inline mr-2" /> Instagram ({igPosts.length})
        </button>
        <button data-testid="admin-tab-artist" onClick={() => setTab("artist")} className={`px-5 py-2 rounded-full text-sm border ${tab === "artist" ? "" : "opacity-60"}`} style={{ borderColor: "var(--kaiya-text)", background: tab === "artist" ? "var(--kaiya-text)" : "transparent", color: tab === "artist" ? "var(--kaiya-bg)" : "var(--kaiya-text)" }}>
          <Film size={14} className="inline mr-2" /> Artist Video
        </button>
      </div>

      {tab === "products" && (
        <div>
          <button data-testid="admin-add-product" onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center gap-2 mb-6">
            <Plus size={16} /> New product
          </button>
          {showForm && (
            <form onSubmit={submit} className="kaiya-card p-6 mb-8 grid md:grid-cols-2 gap-4">
              <input required placeholder="Name" className="kaiya-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <input required placeholder="Slug (url-friendly)" className="kaiya-input" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} />
              <select className="kaiya-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                <option value="apparel">Apparel</option>
                <option value="mats">Mats</option>
                <option value="props">Props</option>
              </select>
              <input required type="number" step="0.01" placeholder="Price (CAD)" className="kaiya-input" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
              <textarea required placeholder="Description" rows="3" className="kaiya-input md:col-span-2" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              <input placeholder="Image URLs (comma-separated)" className="kaiya-input md:col-span-2" value={form.images} onChange={e => setForm({ ...form, images: e.target.value })} />
              <input placeholder="Variants (comma-separated, e.g. XS,S,M,L)" className="kaiya-input md:col-span-2" value={form.variants} onChange={e => setForm({ ...form, variants: e.target.value })} />
              <button type="submit" className="btn-primary md:col-span-2">Create product</button>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map(p => (
              <div key={p.id} className="kaiya-card p-4 flex gap-4">
                <img src={p.images?.[0]} alt={p.name} className="w-20 h-24 object-cover rounded-lg" />
                <div className="flex-1">
                  <div className="label-eyebrow">{p.category}</div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-sm opacity-70">${p.price.toFixed(2)} • {p.active ? "Active" : "Inactive"} • {p.images?.length || 0} photos</div>
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => setEditingImages(editingImages === p.id ? null : p.id)}
                      data-testid={`admin-edit-images-${p.slug}`}
                      className="text-xs underline flex items-center gap-1"
                      style={{ color: "var(--kaiya-primary)" }}
                    >
                      <ImageIcon size={12} /> {editingImages === p.id ? "Close" : "Edit photos"}
                    </button>
                    {p.active && <button onClick={() => deactivate(p.id)} className="text-xs underline opacity-70">Deactivate</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {editingImages && (
            <div className="mt-8 kaiya-card p-6" data-testid="admin-image-editor">
              <div className="flex items-center justify-between mb-4">
                <div className="font-serif-display text-2xl">
                  Photos for {products.find(p => p.id === editingImages)?.name}
                </div>
                <button onClick={() => setEditingImages(null)} className="text-sm underline opacity-70">Done</button>
              </div>
              <ProductImageManager
                productId={editingImages}
                images={products.find(p => p.id === editingImages)?.images || []}
                onChange={reload}
              />
            </div>
          )}
        </div>
      )}

      {tab === "orders" && (
        <OrdersAdmin orders={orders} reload={reload} />
      )}

      {tab === "instagram" && (
        <div>
          <div className="kaiya-card p-6 mb-6" style={{ background: "var(--kaiya-bg-2)" }}>
            <div className="label-eyebrow mb-3">How to add a post</div>
            <ol className="text-sm space-y-2 opacity-80 list-decimal ml-4">
              <li>Open your Instagram post (e.g. instagram.com/p/XYZ/) → right-click the image → &quot;Copy image address&quot; — paste as <strong>Image URL</strong>.</li>
              <li>Copy the post URL from your browser address bar — paste as <strong>Post URL</strong>.</li>
              <li>Order controls display order (lower = first).</li>
            </ol>
            <p className="text-xs opacity-60 mt-3">Note: Instagram CDN image links can expire. For permanent posts, upload the image to Imgur / Cloudinary and paste that URL instead. Automatic sync via Instagram Graph API will land once you provide an access token.</p>
          </div>

          <form onSubmit={submitIg} className="kaiya-card p-6 mb-8 grid md:grid-cols-2 gap-4">
            <input required data-testid="ig-form-image" placeholder="Image URL (jpg/png)" className="kaiya-input md:col-span-2" value={igForm.image_url} onChange={e => setIgForm({ ...igForm, image_url: e.target.value })} />
            <input required data-testid="ig-form-post" placeholder="Instagram post URL" className="kaiya-input md:col-span-2" value={igForm.post_url} onChange={e => setIgForm({ ...igForm, post_url: e.target.value })} />
            <input data-testid="ig-form-caption" placeholder="Caption (optional)" className="kaiya-input" value={igForm.caption} onChange={e => setIgForm({ ...igForm, caption: e.target.value })} />
            <input type="number" data-testid="ig-form-order" placeholder="Order (0 = first)" className="kaiya-input" value={igForm.order} onChange={e => setIgForm({ ...igForm, order: e.target.value })} />
            <button type="submit" data-testid="ig-form-submit" className="btn-primary md:col-span-2">
              <Plus size={16} className="inline mr-2" /> Add Instagram post
            </button>
          </form>

          {igPosts.length === 0 ? (
            <div className="opacity-60 text-center py-12">
              <Instagram size={32} className="mx-auto mb-3 opacity-50" />
              No posts yet. Add your first from @{process.env.REACT_APP_INSTAGRAM_HANDLE} above.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {igPosts.map(p => (
                <div key={p.id} className="relative group" data-testid={`ig-admin-${p.id}`}>
                  <a href={p.post_url} target="_blank" rel="noreferrer" className="image-soft aspect-square block">
                    <img src={p.image_url} alt={p.caption} className="w-full h-full object-cover" />
                  </a>
                  <button
                    onClick={() => deleteIg(p.id)}
                    data-testid={`ig-delete-${p.id}`}
                    className="absolute top-2 right-2 p-2 rounded-full transition hover:scale-110"
                    style={{ background: "var(--kaiya-error)", color: "white" }}
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                  {p.caption && <div className="text-xs opacity-70 mt-2 line-clamp-2">{p.caption}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "artist" && (
        <div data-testid="admin-artist-tab" className="max-w-3xl">
          <div className="kaiya-card p-6 md:p-8 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Film size={22} style={{ color: "var(--kaiya-primary)" }} />
              <h2 className="font-serif-display text-2xl">Artist story video</h2>
            </div>
            <p className="text-sm opacity-70 mb-6">
              A short looping video (5-30 seconds, ideally &lt;20MB) that plays silently at the top of the <strong>/artist</strong> page — perfect for showing Kelsey printing in her studio. MP4 or WebM.
            </p>

            <div className="space-y-4">
              <label className="btn-primary inline-flex items-center gap-2 cursor-pointer" data-testid="admin-video-upload">
                <Film size={16} />
                {videoUploading ? "Uploading…" : (videoUrl ? "Replace video" : "Upload video")}
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  disabled={videoUploading}
                  onChange={(e) => { uploadVideo(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>

              <div className="text-xs opacity-60">— or paste a URL —</div>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  data-testid="admin-video-url-input"
                  placeholder="https://... (mp4/webm/youtube page)"
                  className="kaiya-input flex-1"
                  value={videoInput}
                  onChange={(e) => setVideoInput(e.target.value)}
                />
                <button data-testid="admin-video-save" onClick={saveVideo} className="btn-secondary">Save</button>
              </div>

              {videoUrl && (
                <div className="pt-6 border-t" style={{ borderColor: "var(--kaiya-border)" }}>
                  <div className="label-eyebrow mb-3">Preview</div>
                  <video src={videoUrl} className="w-full max-w-md rounded-2xl" controls autoPlay muted loop playsInline />
                  <button
                    onClick={() => { setVideoInput(""); api.put("/admin/settings/artist-video", { video_url: null }).then(() => { setVideoUrl(""); toast.success("Removed"); }); }}
                    className="mt-3 text-sm underline opacity-70"
                  >
                    Remove video
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
